<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row justify="start" align="center">
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.year_batch"
          :items="batches"
          outlined
          dense
          hide-details="auto"
          label="Batch"
          clearable
        ></v-autocomplete>
      </v-col>

      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program"
          label="Program"
          :items="programs"
          :item-text="
            (item) =>
              `${item.degree.short_name}${
                item.specialization ? ` - ${item.specialization}` : ''
              }`
          "
          return-object
          outlined
          dense
          clearable
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model.trim="form.subject_semester"
          label="Subject Semester"
          outlined
          dense
          clearable
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model.trim="form.usn_id"
          label="USN"
          outlined
          dense
          clearable
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model.number="form.course_code"
          label="Course Code"
          outlined
          dense
          hide-details="auto"
          clearable
        />
      </v-col>
      <v-col cols="12" sm="3" class="d-flex justify-start mt-2">
        <v-btn
          class="accent white--text mr-2"
          :loading="loading"
          @click="search(true)"
        >
          <v-icon left small>mdi-magnify</v-icon>
          Search
        </v-btn>
        <v-btn
          class="accent white--text mr-2"
          :loading="downloadingTemplate"
          @click="downloadTemplate"
        >
          <v-icon left small>mdi-download</v-icon>
          Download
        </v-btn>
        <v-btn class="accent white--text mr-2" @click="openUploadDialog">
          <v-icon left small>mdi-upload</v-icon>
          Upload
        </v-btn>
      </v-col>
    </v-row>

    <!-- ── Table ────────────────────────────────────────────────────────── -->
    <v-row class="mt-3">
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :items="internalMarks"
            :server-items-length="totalRecords"
            :options.sync="tableOptions"
            :footer-props="{ 'items-per-page-options': [10, 50, 100] }"
            :loading="loading"
            fixed-header
            class="secondary row-height-50"
            @update:options="onTableOptionsChange"
          >
            <template #[`item.mark`]="{ item }">
              {{ item.mark ?? "-" }}
            </template>

            <template #[`item.action`]="{ item }">
              <v-icon small color="accent" @click="editMark(item)">
                mdi-pencil
              </v-icon>
            </template>

            <template #no-data>
              <div class="text-center grey--text py-4">
                No internal marks found. Apply filters and click Search.
              </div>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <!-- ══════════════════════════════════════════════════════════════════ -->
    <!-- Upload Dialog                                                      -->
    <!-- ══════════════════════════════════════════════════════════════════ -->
    <v-dialog v-model="uploadDialog" max-width="520" persistent scrollable>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Upload Internal Marks
          <v-spacer></v-spacer>
          <v-btn small icon @click="closeUploadDialog">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-4">
          <v-file-input
            v-model="uploadForm.file"
            label="Select filled CSV"
            outlined
            dense
            accept=".csv"
            hide-details="auto"
            prepend-inner-icon="mdi-file-upload"
            prepend-icon=""
          >
            <template v-slot:selection="{ text }">
              <span class="text-body-2 text-truncate" style="max-width: 100%">
                {{ uploadForm.file ? uploadForm.file.name : text }}
              </span>
            </template>
          </v-file-input>
        </v-card-text>

        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="!canSubmitUpload"
            :loading="saving"
            @click="submitUpload"
          >
            Save Internal Marks
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══════════════════════════════════════════════════════════════════ -->
    <!-- Edit Mark Dialog                                                   -->
    <!-- ══════════════════════════════════════════════════════════════════ -->
    <v-dialog v-model="editDialog" max-width="500" persistent scrollable>
      <v-card v-if="editDialog">
        <v-form ref="form">
          <v-card-title class="font-weight-medium secondary--text primary">
            Edit Internal Mark
            <v-spacer></v-spacer>
            <v-btn small icon @click="editDialog = false">
              <v-icon small color="secondary">mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider />
          <v-card-text>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editingItem.usn_id"
                label="USN"
                outlined
                dense
                hide-details="auto"
                readonly
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editingItem.course_code"
                label="Course Code"
                outlined
                dense
                hide-details="auto"
                readonly
              />
            </v-col>
            <v-col cols="12" sm="12">
              <v-textarea
                v-model="editingItem.course_desc"
                label="Course Name"
                outlined
                dense
                hide-details="auto"
                rows="2"
                readonly
              />
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="editingItem.mark"
                label="Mark*"
                type="number"
                outlined
                dense
                hide-details="auto"
                :rules="[rules.reqNumber(editingItem.mark, 'number')]"
              />
            </v-col>
          </v-card-text>
          <v-card-actions class="pa-3">
            <v-spacer />
            <v-btn color="primary" :loading="savingEdit" @click="saveEdit">
              Save
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { studentInternalMarkHeaders } from "@/constants/headers";
import { rules } from "@/helpers/rules";

export default {
  name: "InternalMarks",

  data() {
    return {
      // ── Filter form ──────────────────────────────────────────────────
      form: {
        program: null,
        subject_semester: "",
        usn_id: "",
        course_code: "",
      },

      // ── Dropdown data ────────────────────────────────────────────────
      programs: [],

      // ── Table ────────────────────────────────────────────────────────
      internalMarks: [],
      loading: false,
      tableSearch: "",
      headers: studentInternalMarkHeaders,
      totalRecords: 0,
      tableOptions: { page: 1, itemsPerPage: 10 },
      isResetting: false,

      // ── Upload dialog ────────────────────────────────────────────────
      uploadDialog: false,
      uploadForm: {
        file: null,
      },
      downloadingTemplate: false,
      saving: false,

      // ── Edit dialog ──────────────────────────────────────────────────
      editDialog: false,
      editingItem: {},
      savingEdit: false,
      rules,
      batches: [],
    };
  },

  computed: {
    canSubmitUpload() {
      return !!this.uploadForm.file;
    },

    searchParams() {
      const { program, ...params } = this.form;
      if (program) {
        params.program_id = program.id;
      }
      return params;
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Internal" }]);
    this.init();
  },

  methods: {
    // ── Init ─────────────────────────────────────────────────────────────
    async init() {
          await Promise.all([ this.getPrograms(), this.generateBatches()]);
      this.search();
    },
    async generateBatches() {
      try {
        const response = await this.$axios.get("/get_year_batch");
        this.batches = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getPrograms() {
      await this.$axios
        .get("/programs")
        .then((res) => {
          this.programs = res.data.data;
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message: err.response?.data?.message || "Failed to load programs",
            color: "error",
          });
        });
    },

    // ── Pagination ───────────────────────────────────────────────────────
    onTableOptionsChange(options) {
      if (this.isResetting) return;
      this.tableOptions = options;
      this.search();
    },

    // ── Search ───────────────────────────────────────────────────────────
    async search(resetPage = false) {
      if (resetPage) {
        this.isResetting = true;
        this.tableOptions = { ...this.tableOptions, page: 1 };
        this.$nextTick(() => {
          this.isResetting = false;
        });
      }

      this.loading = true;
      this.internalMarks = [];
      const queryString = this.objectToQueryParams({
        ...this.searchParams,
        page: this.tableOptions.page,
        per_page: this.tableOptions.itemsPerPage,
      });

      await this.$axios
        .get(`/internal_marks?${queryString}`)
        .then((res) => {
          this.internalMarks = res.data.data.records;
          this.totalRecords = res.data.data.total;
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message:
              err.response?.data?.message || "Failed to load internal marks",
            color: "error",
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // ── Upload dialog ────────────────────────────────────────────────────
    openUploadDialog() {
      this.uploadForm.file = null;
      this.uploadDialog = true;
    },

    closeUploadDialog() {
      this.uploadDialog = false;
      this.uploadForm.file = null;
    },

    objectToQueryParams(obj) {
      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }
      return Object.keys(obj)
        .filter(
          (key) =>
            obj[key] !== undefined && obj[key] !== null && obj[key] !== "",
        )
        .map(
          (key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]),
        )
        .join("&");
    },

    async downloadTemplate() {
      this.downloadingTemplate = true;

      const queryString = this.objectToQueryParams({
        ...this.searchParams,
        type: "internal",
      });

      await this.$axios
        .get(`/download_marks?${queryString}`, { responseType: "blob" })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `Internal Marks.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message: err.response?.data?.message || "Download failed",
            color: "error",
          });
        })
        .finally(() => {
          this.downloadingTemplate = false;
        });
    },

    async submitUpload() {
      if (!this.uploadForm.file) return;
      this.saving = true;

      const formData = new FormData();
      formData.append("file", this.uploadForm.file, this.uploadForm.file.name);
      formData.append("type", "internal");
      formData.append("save", "submit");

      await this.$axios
        .post("/upload_marks", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
          validateStatus: (s) => s < 500,
        })
        .then((res) => {
          // 4xx = validation error (e.g. bad CSV headers) — parse JSON from blob
          if (res.status >= 400) {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const data = JSON.parse(reader.result);
                this.$store.commit("showSnackbar", {
                  message: data.errors || data.message || "Upload failed",
                  color: "error",
                });
              } catch {
                this.$store.commit("showSnackbar", {
                  message: "Upload failed",
                  color: "error",
                });
              }
            };
            reader.readAsText(res.data);
            return;
          }

          // 201 = row-level errors — download error CSV
          if (res.status === 201) {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `internal_errors.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            res.data.text().then((text) => {
              const rows = text.trim().split("\n").length - 1;
              this.$store.commit("showSnackbar", {
                message: `${rows} row(s) have errors. Nothing was saved. See the downloaded file.`,
                color: "error",
              });
            });
            return;
          }

          // 200 = success
          res.data.text().then((text) => {
            let body = {};
            try {
              body = JSON.parse(text);
            } catch {
              body = {};
            }
            const paidLater = body.paid_later_attempts || [];
            // Courses with no internal component (e.g. I0-E50 labs) are skipped.
            const skipped = (body.skipped_no_internal || [])
              .map((s) => `${s.course_code} (${s.rows})`)
              .join(", ");
            let message = "Internal marks saved successfully";
            if (skipped) message += `. Skipped, no internal mark for: ${skipped}`;
            this.$store.commit("showSnackbar", {
              message: this.savedMessage(message, paidLater),
              color: paidLater.length ? "warning" : "success",
            });
          });
          this.closeUploadDialog();
          this.search();
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message: err.response?.data?.message || "Upload failed",
            color: "error",
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },

    // A paper now passed whose newer attempt is already paid: that attempt is left
    // open for someone to refund or cancel.
    savedMessage(message, paidLater) {
      if (!paidLater.length) return message;
      const rows = paidLater
        .map((p) => `${p.usn_id} ${p.course_code} (${p.exam_batch})`)
        .join(", ");
      return `${message}. Paper passed but a paid later attempt is still open: ${rows}`;
    },

    // ── Edit ─────────────────────────────────────────────────────────────
    editMark(item) {
      this.editingItem = { ...item };
      this.editDialog = true;
    },

    async saveEdit() {
      if (!this.$refs.form.validate()) return;

      this.savingEdit = true;
      await this.$axios
        .put(`/internal_marks/${this.editingItem.id}`, {
          internal_mark: { mark: this.editingItem.mark },
        })
        .then((res) => {
          const paidLater = res.data?.paid_later_attempts || [];
          this.$store.commit("showSnackbar", {
            message: this.savedMessage("Internal mark updated", paidLater),
            color: paidLater.length ? "warning" : "success",
          });
          this.editDialog = false;
          this.search();
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message:
              (typeof err.response?.data?.errors === "string" &&
                err.response.data.errors) ||
              err.response?.data?.message ||
              "Update failed",
            color: "error",
          });
        })
        .finally(() => {
          this.savingEdit = false;
        });
    },
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
